import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Trash2, Eye, Edit2, ToggleLeft, ToggleRight, DollarSign, Tag, Image as ImageIcon } from 'lucide-react';
import { AIAgentScenario, deleteScenario, updateScenario } from '../../lib/aiAgents';
import toast from 'react-hot-toast';

interface AgentScenarioCardProps {
  scenario: AIAgentScenario;
  isNew?: boolean;
  onDeleted: () => void;
  onUpdated: () => void;
  onEdit: (scenarioId: string) => void;
}

export default function AgentScenarioCard({ scenario, isNew, onDeleted, onUpdated, onEdit }: AgentScenarioCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Delete this scenario?')) return;
    setDeleting(true);
    try {
      await deleteScenario(scenario.id);
      toast.success('Scenario deleted');
      onDeleted();
    } catch {
      toast.error('Failed to delete scenario');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await updateScenario(scenario.id, { is_active: !scenario.is_active });
      onUpdated();
    } catch {
      toast.error('Failed to update scenario');
    }
  };

  const totalEstimate = scenario.outcome?.total_estimate;
  const lineItems = scenario.outcome?.line_items || [];
  const factors = scenario.outcome?.factors_considered || [];
  const messageCount = scenario.messages?.length || 0;
  const imageCount = scenario.images?.length || 0;
  const isUnsaved = scenario.title === 'New Scenario' && !scenario.outcome;

  return (
    <div
      className={`rounded-lg border transition-all duration-200 ${
        isNew ? 'border-blue-300 bg-blue-50/50 animate-pulse-once' : ''
      } ${
        scenario.is_active
          ? 'border-gray-200 bg-white hover:border-gray-300'
          : 'border-gray-100 bg-gray-50 opacity-60'
      }`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-2.5 flex items-start gap-2 text-left"
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium truncate ${scenario.is_active ? 'text-gray-900' : 'text-gray-500'}`}>
              {isUnsaved ? 'Unsaved Scenario' : scenario.title}
            </span>
            {isUnsaved && (
              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                IN PROGRESS
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 mt-1">
            {totalEstimate != null && (
              <span className="text-xs text-green-600 font-medium flex items-center gap-0.5">
                <DollarSign className="h-3 w-3" />
                {typeof totalEstimate === 'number' ? totalEstimate.toFixed(2) : totalEstimate}
              </span>
            )}
            {imageCount > 0 && (
              <span className="text-xs text-gray-400 flex items-center gap-0.5">
                <ImageIcon className="h-3 w-3" />
                {imageCount}
              </span>
            )}
            <span className="text-xs text-gray-400">
              {messageCount} messages
            </span>
          </div>

          {scenario.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {scenario.tags.slice(0, 4).map((tag, i) => (
                <span key={i} className="inline-flex items-center gap-0.5 text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                  <Tag className="h-2 w-2" />
                  {tag}
                </span>
              ))}
              {scenario.tags.length > 4 && (
                <span className="text-[10px] text-gray-400">+{scenario.tags.length - 4}</span>
              )}
            </div>
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 border-t border-gray-100">
          {scenario.description && (
            <p className="text-xs text-gray-600 mt-2 leading-relaxed">{scenario.description}</p>
          )}

          {scenario.images?.length > 0 && (
            <div className="flex gap-1.5 mt-2 overflow-x-auto">
              {scenario.images.slice(0, 4).map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt={`Scenario photo ${i + 1}`}
                  className="h-16 w-16 rounded-md object-cover flex-shrink-0 border border-gray-200"
                />
              ))}
              {scenario.images.length > 4 && (
                <div className="h-16 w-16 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0 border border-gray-200">
                  <span className="text-xs text-gray-500">+{scenario.images.length - 4}</span>
                </div>
              )}
            </div>
          )}

          {lineItems.length > 0 && (
            <div className="mt-2 space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Estimate Breakdown</p>
              {lineItems.map((item: any, i: number) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-gray-600 truncate mr-2">{item.description}</span>
                  <span className="text-gray-900 font-medium flex-shrink-0">${typeof item.amount === 'number' ? item.amount.toFixed(2) : item.amount}</span>
                </div>
              ))}
              {totalEstimate != null && (
                <div className="flex justify-between text-xs border-t border-gray-100 pt-1 mt-1">
                  <span className="text-gray-900 font-semibold">Total</span>
                  <span className="text-green-600 font-semibold">${typeof totalEstimate === 'number' ? totalEstimate.toFixed(2) : totalEstimate}</span>
                </div>
              )}
            </div>
          )}

          {factors.length > 0 && (
            <div className="mt-2">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Factors Considered</p>
              <div className="flex flex-wrap gap-1">
                {factors.map((f: string, i: number) => (
                  <span key={i} className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{f}</span>
                ))}
              </div>
            </div>
          )}

          {scenario.outcome?.reasoning && (
            <p className="text-xs text-gray-500 mt-2 italic leading-relaxed">{scenario.outcome.reasoning}</p>
          )}

          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100">
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(scenario.id); }}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 transition-colors"
            >
              {isUnsaved ? <Eye className="h-3 w-3" /> : <Edit2 className="h-3 w-3" />}
              {isUnsaved ? 'Continue' : 'Edit'}
            </button>
            <button
              onClick={handleToggleActive}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              {scenario.is_active ? <ToggleRight className="h-3 w-3" /> : <ToggleLeft className="h-3 w-3" />}
              {scenario.is_active ? 'Active' : 'Inactive'}
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 transition-colors ml-auto disabled:opacity-50"
            >
              <Trash2 className="h-3 w-3" />
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
